import React, { useMemo } from 'react';
import { Award, TrendingUp, Zap, Star, ChevronRight, Calendar } from 'lucide-react';

const TIERS = [
  {
    key: 'partner',
    label: 'AllFlowX Partner',
    short: 'Partner',
    min: 0,
    max: 200,
    rate: 0.10,
    commission: '10%',
    color: { bg: 'from-blue-500 to-blue-700', badge: 'bg-blue-100 text-blue-700', bar: '#3b82f6', glow: 'rgba(59,130,246,0.35)', ring: 'ring-blue-400' },
    icon: Star,
  },
  {
    key: 'business',
    label: 'AllFlowX Business',
    short: 'Business',
    min: 200.01,
    max: 400,
    rate: 0.15,
    commission: '15%',
    color: { bg: 'from-emerald-500 to-teal-600', badge: 'bg-emerald-100 text-emerald-700', bar: '#10b981', glow: 'rgba(16,185,129,0.35)', ring: 'ring-emerald-400' },
    icon: TrendingUp,
  },
  {
    key: 'elite',
    label: 'AllFlowX Elite',
    short: 'Elite',
    min: 400.01,
    max: Infinity,
    rate: 0.20,
    commission: '20%',
    color: { bg: 'from-amber-400 to-orange-500', badge: 'bg-amber-100 text-amber-700', bar: '#f59e0b', glow: 'rgba(245,158,11,0.35)', ring: 'ring-amber-400' },
    icon: Zap,
  },
];

const formatEur = (v) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v || 0);

const CommissionPreviewWidget = ({ currentMonthRevenue = 0, currentMonthEarnings = 0 }) => {
  const { currentTier, nextTier, progress, remaining, projectedRevenue, projectedCommission, daysRemaining } = useMemo(() => {
    const rev = currentMonthRevenue || 0;

    // Determine current tier
    const tier = TIERS.find(t => rev >= t.min && (t.max === Infinity || rev <= t.max)) || TIERS[0];
    const next = TIERS.find(t => t.min > rev) || null;

    // Progress to next tier
    let prog = 0;
    let rem = 0;
    if (next) {
      prog = ((rev - tier.min) / (next.min - tier.min)) * 100;
      rem = next.min - rev;
    } else {
      prog = 100;
    }

    // Forecast: daily pace → projected end-of-month
    const now = new Date();
    const dayOfMonth = now.getDate();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const daysLeft = lastDay - dayOfMonth;
    const dailyPace = dayOfMonth > 0 ? rev / dayOfMonth : 0;
    const projected = dailyPace * lastDay;

    // Projected commission
    const projTier = TIERS.find(t => projected >= t.min && (t.max === Infinity || projected <= t.max)) || TIERS[0];
    const projComm = projected * projTier.rate;

    return {
      currentTier: tier,
      nextTier: next,
      progress: Math.min(prog, 100),
      remaining: rem,
      projectedRevenue: projected,
      projectedCommission: projComm,
      daysRemaining: daysLeft,
    };
  }, [currentMonthRevenue]);

  const TierIcon = currentTier.icon;

  return (
    <div
      data-testid="commission-preview-widget"
      className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden mb-6"
    >
      {/* Top gradient header */}
      <div className={`bg-gradient-to-r ${currentTier.color.bg} p-4 sm:p-6`}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
              <TierIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-white/80 text-xs font-medium uppercase tracking-widest">Aktuelle Stufe</p>
              <h2
                data-testid="current-tier-name"
                className="text-xl sm:text-2xl font-bold text-white"
              >
                {currentTier.label}
              </h2>
            </div>
          </div>

          <div className="text-right">
            <p className="text-white/80 text-xs">Provisionssatz</p>
            <p
              data-testid="current-commission-rate"
              className="text-3xl sm:text-4xl font-black text-white"
            >
              {currentTier.commission}
            </p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 sm:p-6 space-y-6">
        {/* Progress to next tier */}
        <div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2 gap-1">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Fortschritt diesen Monat
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {formatEur(currentMonthRevenue)}
              {nextTier && <span className="text-gray-400"> / {formatEur(nextTier.min)}</span>}
            </span>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-4 overflow-hidden relative">
            <div
              data-testid="tier-progress-bar"
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{
                width: `${progress}%`,
                background: `linear-gradient(90deg, ${currentTier.color.bar}, ${nextTier ? nextTier.color.bar : currentTier.color.bar})`,
                boxShadow: `0 0 8px ${currentTier.color.glow}`,
              }}
            />
          </div>

          <div className="mt-2">
            {nextTier ? (
              <>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  Noch{' '}
                  <span
                    data-testid="remaining-to-next-tier"
                    className="font-bold"
                    style={{ color: currentTier.color.bar }}
                  >
                    {formatEur(remaining)}
                  </span>{' '}
                  bis{' '}
                  <span className="font-semibold">{nextTier.label}</span>{' '}
                  <span className="font-bold">({nextTier.commission})</span>
                </p>

                {/* Upgrade Banner */}
                <div
                  data-testid="upgrade-banner"
                  className="mt-3 flex items-center gap-3 rounded-xl px-4 py-3"
                  style={{
                    background: `linear-gradient(135deg, ${currentTier.color.bar}18, ${nextTier.color.bar}18)`,
                    border: `1.5px solid ${nextTier.color.bar}55`,
                  }}
                >
                  <div
                    className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: `${nextTier.color.bar}25` }}
                  >
                    <nextTier.icon className="w-4 h-4" style={{ color: nextTier.color.bar }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold" style={{ color: nextTier.color.bar }}>
                      Nur noch{' '}
                      <span data-testid="upgrade-banner-remaining" className="font-black">
                        {formatEur(remaining)}
                      </span>{' '}
                      bis{' '}
                      <span className="font-black">{nextTier.label}</span>!
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Erhalte danach <span className="font-bold">{nextTier.commission}</span> auf deinen gesamten Monatsumsatz.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <p data-testid="elite-reached-msg" className="text-xs sm:text-sm font-semibold text-amber-600 dark:text-amber-400">
                Höchste Stufe erreicht — 20 % auf den gesamten Monatsumsatz!
              </p>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
          {/* This month earnings */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 sm:p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Provision (aktuell)</p>
            <p
              data-testid="current-month-earnings"
              className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100"
            >
              {formatEur(currentMonthEarnings)}
            </p>
          </div>

          {/* Forecast */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 sm:p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Prognose (Monatsende)</p>
            <p
              data-testid="projected-commission"
              className="text-lg sm:text-xl font-bold"
              style={{ color: currentTier.color.bar }}
            >
              {formatEur(projectedCommission)}
            </p>
          </div>

          {/* Days remaining */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 sm:p-4 col-span-2 sm:col-span-1">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-3 h-3 text-gray-400" />
              <p className="text-xs text-gray-500 dark:text-gray-400">Tage verbleibend</p>
            </div>
            <p
              data-testid="days-remaining"
              className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100"
            >
              {daysRemaining}
            </p>
          </div>
        </div>

        {/* Tier roadmap */}
        <div>
          <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
            Stufen-Übersicht
          </p>
          <div className="flex items-center gap-1 sm:gap-2">
            {TIERS.map((tier, i) => {
              const isActive = tier.key === currentTier.key;
              const isPassed = TIERS.indexOf(currentTier) > i;
              const TIcon = tier.icon;
              return (
                <React.Fragment key={tier.key}>
                  <div
                    data-testid={`tier-item-${tier.key}`}
                    className={`flex-1 rounded-xl p-2 sm:p-3 border-2 transition-all ${
                      isActive
                        ? `${tier.color.badge} border-current ring-2 ${tier.color.ring} shadow-md`
                        : isPassed
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 opacity-80'
                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-50'
                    }`}
                  >
                    <TIcon
                      className={`w-4 h-4 mb-1 ${isActive ? '' : isPassed ? 'text-green-500' : 'text-gray-400'}`}
                      style={isActive ? { color: tier.color.bar } : undefined}
                    />
                    <p className="text-xs font-bold text-gray-900 dark:text-gray-100 leading-tight">
                      {tier.short}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{tier.commission}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {tier.max === Infinity ? `ab ${formatEur(tier.min)}` : `bis ${formatEur(tier.max)}`}
                    </p>
                  </div>
                  {i < TIERS.length - 1 && (
                    <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommissionPreviewWidget;
